import {
  createContext,
  useContext,
  useState,
  useCallback,
  type ReactNode,
} from "react";

type Lang = "en" | "zh";

const STORAGE_KEY = "ideaweave-landing-lang";

function detectLang(): Lang {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored === "en" || stored === "zh") {
      return stored;
    }
  } catch {
    // localStorage unavailable
  }
  return (navigator.language || "").startsWith("zh") ? "zh" : "en";
}

// ─── Translation dictionaries ─────────────────────────────────────────────────

const en: Record<string, string> = {
  "nav.features": "Features",
  "nav.cta": "Try IdeaWeave",

  "hero.badge": "AI-Powered Whiteboard for Creators",
  "hero.subtitle":
    "Speak your idea. AI brainstorms, organizes, and draws \u2014 then you record & present it, all on one canvas.",
  "hero.primaryCta": "Start Creating",
  "hero.ghostCta": "See How It Works",
  "hero.scroll": "Scroll",
  "hero.demoLabel": "Examples",
  "hero.watchDemo": "Watch Demo",
  "hero.demoDuration": "1.5 min",

  "draw.showcase.tag": "Quick Draw",
  "draw.showcase.heading": "From idea to diagram, instantly",
  "draw.showcase.desc":
    "No brainstorming, no multi-step workflow. Just type what you want and get a professional diagram in seconds.",
  "draw.showcase.step": "Type Your Idea",
  "draw.showcase.stepDesc":
    "Describe any concept \u2014 a system architecture, a flowchart, a mind map \u2014 and AI draws it for you, instantly.",
  "draw.showcase.inputPlaceholder":
    "Draw a flowchart about machine learning pipeline...",
  "draw.showcase.drawLabel": "Draw",
  "draw.showcase.drawDesc": "Quick Diagram",
  "draw.showcase.weaveLabel": "Weave",
  "draw.showcase.weaveDesc": "Creator Studio",

  "switcher.title": "Two modes, one canvas",
  "switcher.subtitle":
    "Quick diagrams or deep creation \u2014 choose the workflow that fits your need.",

  "weave.showcase.tag": "Weave Studio",
  "weave.showcase.heading": "The complete creative journey",
  "weave.showcase.desc":
    "Brainstorm, organize, draw, and present \u2014 an end-to-end workflow powered by AI, all on one canvas.",

  "steps.tag": "How It Works",
  "steps.heading": "Weave: Idea to presentation in 4 steps",

  "step1.label": "Bring Your Topic",
  "step1.title": "Speak or type your idea",
  "step1.desc":
    "Just talk naturally \u2014 describe the concept you want to explain. IdeaWeave captures your voice and turns it into a starting point.",

  "step2.label": "AI Brainstorm",
  "step2.title": "Chat with AI to shape your idea",
  "step2.desc":
    "Brainstorm in conversation\u2014follow-ups, clarifications, and research. When you\u2019re ready, tap Go to Draw in the corner to continue on the canvas.",

  "step3.label": "Auto Drawing",
  "step3.title": "Watch it come alive on canvas",
  "step3.desc":
    "On the canvas, your thread becomes a professional diagram. Edit anything: drag, resize, restyle, or ask AI to refine it.",

  "step4.label": "Record & Present",
  "step4.title": "Present with AI teleprompter",
  "step4.desc":
    "Turn on recording mode. AI generates a presentation script, you see it in the teleprompter, and record yourself explaining the diagram with your camera.",

  "timeline.title": "One seamless flow",
  "timeline.speak": "Speak",
  "timeline.brainstorm": "Brainstorm",
  "timeline.draw": "Draw",
  "timeline.present": "Present",

  "cta.title": "Ready to Weave Your Ideas?",
  "cta.btn": "Start Creating \u2014 It\u2019s Free",

  "footer.privacy": "Privacy",
  "footer.terms": "Terms",
};

const zh: Record<string, string> = {
  "nav.features": "功能",
  "nav.cta": "开始使用",

  "hero.badge": "AI 驱动的创意白板",
  "hero.subtitle":
    "说出你的想法，AI 帮你头脑风暴、整理结构、自动画图\u2014\u2014然后录制讲解，一块画布搞定一切。",
  "hero.primaryCta": "开始创作",
  "hero.ghostCta": "了解工作流程",
  "hero.scroll": "向下滚动",
  "hero.demoLabel": "示例",
  "hero.watchDemo": "观看演示",
  "hero.demoDuration": "1.5 分钟",

  "draw.showcase.tag": "快速画图",
  "draw.showcase.heading": "从想法到图表，一步到位",
  "draw.showcase.desc":
    "无需头脑风暴，无需多步流程。只需输入你的想法，几秒钟就能获得专业图表。",
  "draw.showcase.step": "输入你的想法",
  "draw.showcase.stepDesc":
    "描述任何概念\u2014\u2014系统架构、流程图、思维导图\u2014\u2014AI 为你即时绘制。",
  "draw.showcase.inputPlaceholder": "画一个关于机器学习流水线的流程图...",
  "draw.showcase.drawLabel": "Draw",
  "draw.showcase.drawDesc": "快速画图",
  "draw.showcase.weaveLabel": "Weave",
  "draw.showcase.weaveDesc": "创作工坊",

  "switcher.title": "两种模式，一块画布",
  "switcher.subtitle": "快速画图还是深度创作\u2014\u2014选择最适合你的工作流。",

  "weave.showcase.tag": "创作工坊",
  "weave.showcase.heading": "完整的创作之旅",
  "weave.showcase.desc":
    "头脑风暴、整理结构、自动画图、录制演示\u2014\u2014AI 驱动的端到端工作流，一块画布搞定一切。",

  "steps.tag": "工作流程",
  "steps.heading": "Weave：从想法到演示，只需 4 步",

  "step1.label": "提出话题",
  "step1.title": "说出或输入你的想法",
  "step1.desc":
    "自然地说话\u2014\u2014描述你想讲解的概念。IdeaWeave 捕捉你的语音，将其转化为起点。",

  "step2.label": "AI 头脑风暴",
  "step2.title": "与 AI 对话，打磨想法",
  "step2.desc":
    "在对话里头脑风暴、追问澄清、补充信息。准备好后，点击右下角的「去画图」进入画布继续。",

  "step3.label": "自动绘图",
  "step3.title": "看想法在画布上成型",
  "step3.desc":
    "在画布上，对话会延伸为专业图表。随意编辑：拖拽、调整大小、更改样式，或让 AI 帮你优化。",

  "step4.label": "录制演示",
  "step4.title": "配合 AI 提词器进行演示",
  "step4.desc":
    "打开录制模式。AI 自动生成演讲稿，你在提词器中看到文字，同时用摄像头录制自己讲解图表的过程。",

  "timeline.title": "一个流畅的工作流",
  "timeline.speak": "说",
  "timeline.brainstorm": "想",
  "timeline.draw": "画",
  "timeline.present": "讲",

  "cta.title": "准备好编织你的想法了吗？",
  "cta.btn": "免费开始创作",

  "footer.privacy": "隐私",
  "footer.terms": "条款",
};

// Last word in each array gets the character-by-character wave animation
const heroWordsMap: Record<Lang, string[]> = {
  en: ["Explain", "Any", "Idea,", "Visually"],
  zh: ["解释", "一切", "想法，", "可视化"],
};

// ─── Context ──────────────────────────────────────────────────────────────────

interface LandingI18n {
  lang: Lang;
  toggleLang: () => void;
  t: (key: string) => string;
  heroWords: string[];
}

const Ctx = createContext<LandingI18n | null>(null);

export const LandingI18nProvider = ({ children }: { children: ReactNode }) => {
  const [lang, setLang] = useState<Lang>(detectLang);

  const toggleLang = useCallback(() => {
    setLang((prev) => {
      const next: Lang = prev === "en" ? "zh" : "en";
      try {
        localStorage.setItem(STORAGE_KEY, next);
      } catch {
        // ignore
      }
      return next;
    });
  }, []);

  const t = useCallback(
    (key: string) => (lang === "zh" ? zh : en)[key] ?? en[key] ?? key,
    [lang],
  );

  return (
    <Ctx.Provider
      value={{ lang, toggleLang, t, heroWords: heroWordsMap[lang] }}
    >
      {children}
    </Ctx.Provider>
  );
};

export const useLandingI18n = () => {
  const ctx = useContext(Ctx);
  if (!ctx) {
    throw new Error("useLandingI18n requires LandingI18nProvider");
  }
  return ctx;
};
